/**
 * The three things every "the API answers a file" route needs, written once.
 *
 * `GET /me/archive` was the first and wrote them inline; `GET
 * /certificates/{serial}/pdf` is the second. Two copies of "make an anchor,
 * click it, revoke later" is exactly the shape where one gets the Safari
 * revoke timing right and the other does not, so they live here and the
 * per-route modules keep only what differs: the fallback filename and the
 * refusal keys.
 */
/**
 * The server's own filename, or `fallback()` when it cannot be read.
 *
 * `Content-Disposition` is not CORS-safelisted, so this reads `null` on a
 * cross-origin call unless the server exposes it — which it does globally
 * since round 18, so the fallback should now never fire. It stays at the
 * backend's own request: a header that fails to parse must not leave a member
 * with no file.
 *
 * The quoted form is what the routes send; the bare form is accepted because
 * the grammar allows it and a filename is not worth a refusal.
 */
export function filenameFromDisposition(disposition, fallback) {
    if (!disposition)
        return fallback();
    // `[^"]*`, not `+`: an empty `filename=""` must reach the length check below
    // rather than falling through to the unquoted branch, which would happily
    // match the two quote characters and name the file `""`.
    const match = /filename\s*=\s*(?:"([^"]*)"|([^;]+))/i.exec(disposition);
    const name = (match?.[1] ?? match?.[2])?.trim();
    return name && name.length > 0 ? name : fallback();
}
/** How long an object URL outlives its click. A same-tick revoke cancels the download in Safari. */
export const REVOKE_AFTER_MS = 10_000;
/**
 * Hand a blob to the browser as a saved file.
 *
 * The anchor is never added to the document — a click on a detached element
 * dispatches exactly the same way, and appending it risks a frame of a stray
 * link. The revoke is deferred rather than immediate for Safari's sake.
 */
export function saveBlob(blob, filename) {
    const url = URL.createObjectURL(blob);
    try {
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = filename;
        anchor.click();
    }
    finally {
        setTimeout(() => URL.revokeObjectURL(url), REVOKE_AFTER_MS);
    }
}
/**
 * A server-declared route path, made callable through `HttpClient`.
 *
 * The API names its own routes absolutely — `pdfPath: "/api/certificates/LB-000042/pdf"`
 * — and the contract says clients must call what it names rather than
 * assemble the path themselves. But the client is built on `VITE_API_URL`,
 * which already IS `/api`, so every path it takes is relative to that prefix.
 * Passing the declared path straight through would call `/api/api/…`.
 *
 * So the prefix is stripped here, once, and nowhere else. Only a leading
 * `/api` followed by a slash (or the end) is removed: `/apix/…` and a path
 * without the prefix pass through untouched, so a server that one day drops
 * the prefix from its declarations keeps working.
 */
export function apiRelativePath(declaredPath) {
    const match = /^\/api(?=\/|$)/.exec(declaredPath);
    if (!match)
        return declaredPath;
    const rest = declaredPath.slice(match[0].length);
    return rest === '' ? '/' : rest;
}
//# sourceMappingURL=download.js.map